import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/App";
import { api, API_BASE, formatError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import {
  QrCode,
  Plus,
  Search,
  MoreHorizontal,
  Download,
  Lock,
  Unlock,
  Pencil,
  LogOut,
  ExternalLink,
  Copy,
  Trash2,
} from "lucide-react";
import CreateQRDialog from "@/components/CreateQRDialog";
import EditQRDialog from "@/components/EditQRDialog";
import ScanQRDialog from "@/components/ScanQRDialog";
import { ScanLine } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS_META = {
  aktif: { label: "Aktif", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30" },
  kosong: { label: "Kosong", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30" },
  terkunci: { label: "Terkunci", cls: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30" },
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, kosong: 0, aktif: 0, terkunci: 0 });
  const [status, setStatus] = useState("semua");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status && status !== "semua") params.status = status;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/qrs", { params });
      setItems(data.items);
      setStats(data.stats);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  }, [status, q]);

  useEffect(() => {
    load();
  }, [load]);

  const handleLock = async (row) => {
    try {
      await api.post(`/qrs/${row.id}/lock`);
      toast.success(`QR ${row.code} dikunci`);
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };
  const handleUnlock = async (row) => {
    try {
      await api.post(`/qrs/${row.id}/unlock`);
      toast.success(`QR ${row.code} dibuka`);
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/qrs/${deleteTarget.id}`);
      toast.success(`QR ${deleteTarget.code} dihapus`);
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setDeleting(false);
    }
  };

  const handleDownload = async (row) => {
    try {
      const token = localStorage.getItem("jscan_token");
      const res = await fetch(`${API_BASE}/qrs/${row.id}/png`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Gagal download");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `jscan-${row.code}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "Gagal download");
    }
  };

  const copyScanUrl = (row) => {
    const url = `${window.location.origin}/q/${row.code}`;
    navigator.clipboard.writeText(url);
    toast.success("URL scan disalin");
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0B0F17]" data-testid="dashboard-page">
      {/* Top Bar */}
      <header className="sticky top-0 z-20 border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-[#0B0F17]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-sky-500 flex items-center justify-center">
              <QrCode className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <div className="jscan-brand text-lg leading-none">J-scan</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Admin Console</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-sm text-muted-foreground jscan-mono">@{user?.username}</div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { logout(); toast.info("Kamu telah keluar"); }}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" /> Keluar
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-8 space-y-6 md:space-y-8 fade-in">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="jscan-brand text-3xl md:text-4xl">Manajemen QR</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Kelola QR akrilik review UMKM. Cetak sekali, ubah tujuan kapan saja.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setScanOpen(true)}
              data-testid="open-scan-qr-button"
              className="rounded-full h-11 px-5 border-sky-500/40 text-sky-700 dark:text-sky-300 hover:bg-sky-50 dark:hover:bg-sky-500/10"
            >
              <ScanLine className="w-4 h-4 mr-2" /> Scan QR
            </Button>
            <Button
              onClick={() => setCreateOpen(true)}
              data-testid="open-create-qr-button"
              className="rounded-full bg-sky-600 hover:bg-sky-700 h-11 px-5 text-white shadow-md shadow-sky-500/20"
            >
              <Plus className="w-4 h-4 mr-2" /> Buat QR Baru
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {[
            { key: "total", label: "Total QR", value: stats.total, cls: "text-slate-900 dark:text-white" },
            { key: "aktif", label: "Aktif", value: stats.aktif, cls: "text-emerald-600 dark:text-emerald-400" },
            { key: "kosong", label: "Kosong", value: stats.kosong, cls: "text-amber-600 dark:text-amber-400" },
            { key: "terkunci", label: "Terkunci", value: stats.terkunci, cls: "text-rose-600 dark:text-rose-400" },
          ].map((s) => (
            <div
              key={s.key}
              className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 p-5"
              data-testid={`stat-${s.key}`}
            >
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{s.label}</div>
              <div className={`jscan-brand text-3xl md:text-4xl mt-2 ${s.cls}`}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Filter Bar */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari kode atau label toko..."
              className="pl-9 h-11 rounded-full"
              data-testid="search-input"
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="sm:w-52 h-11 rounded-full" data-testid="status-filter">
              <SelectValue placeholder="Semua status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="semua" data-testid="filter-semua">Semua status</SelectItem>
              <SelectItem value="aktif" data-testid="filter-aktif">Aktif</SelectItem>
              <SelectItem value="kosong" data-testid="filter-kosong">Kosong</SelectItem>
              <SelectItem value="terkunci" data-testid="filter-terkunci">Terkunci</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="qr-table">
              <thead className="text-xs uppercase tracking-wider text-muted-foreground bg-slate-50 dark:bg-slate-900/60">
                <tr>
                  <th className="text-left px-5 py-3">Kode</th>
                  <th className="text-left px-5 py-3">Label Toko</th>
                  <th className="text-left px-5 py-3">Tujuan</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-left px-5 py-3">Dibuat</th>
                  <th className="text-right px-5 py-3">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-16 text-center text-muted-foreground">
                      Memuat data...
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-16 text-center">
                      <div className="jscan-brand text-xl">Belum ada QR</div>
                      <p className="text-sm text-muted-foreground mt-2">
                        Klik "Buat QR Baru" untuk mulai.
                      </p>
                    </td>
                  </tr>
                ) : (
                  items.map((row) => {
                    const meta = STATUS_META[row.status];
                    return (
                      <tr
                        key={row.id}
                        className="border-t border-slate-100 dark:border-slate-800/60 hover:bg-slate-50/70 dark:hover:bg-slate-900/40"
                        data-testid={`qr-row-${row.code}`}
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            <span className="jscan-mono font-semibold">{row.code}</span>
                            <button
                              onClick={() => copyScanUrl(row)}
                              className="text-muted-foreground hover:text-sky-600"
                              title="Salin URL scan"
                              data-testid={`copy-url-${row.code}`}
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                        <td className="px-5 py-4 max-w-[220px] truncate">
                          {row.label || <span className="text-muted-foreground italic">—</span>}
                        </td>
                        <td className="px-5 py-4 max-w-[280px]">
                          {row.destination_url ? (
                            <a
                              href={row.destination_url}
                              target="_blank"
                              rel="noreferrer"
                              className="text-sky-600 dark:text-sky-400 hover:underline flex items-center gap-1 truncate"
                            >
                              <span className="truncate">{row.destination_url}</span>
                              <ExternalLink className="w-3 h-3 flex-shrink-0" />
                            </a>
                          ) : (
                            <span className="text-muted-foreground italic">Belum diisi</span>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <Badge variant="outline" className={`${meta.cls} font-medium`}>
                            {row.status === "terkunci" && <Lock className="w-3 h-3 mr-1" />}
                            {meta.label}
                          </Badge>
                        </td>
                        <td className="px-5 py-4 text-muted-foreground jscan-mono text-xs">
                          {new Date(row.created_at).toLocaleDateString("id-ID", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </td>
                        <td className="px-5 py-4 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                data-testid={`row-actions-${row.code}`}
                              >
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-52">
                              <DropdownMenuItem
                                onClick={() => setEditTarget(row)}
                                disabled={row.locked}
                                data-testid={`action-edit-${row.code}`}
                              >
                                <Pencil className="w-4 h-4 mr-2" /> Edit tujuan
                              </DropdownMenuItem>
                              {row.locked ? (
                                <DropdownMenuItem
                                  onClick={() => handleUnlock(row)}
                                  data-testid={`action-unlock-${row.code}`}
                                >
                                  <Unlock className="w-4 h-4 mr-2" /> Buka kunci
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem
                                  onClick={() => handleLock(row)}
                                  disabled={!row.destination_url}
                                  data-testid={`action-lock-${row.code}`}
                                >
                                  <Lock className="w-4 h-4 mr-2" /> Kunci
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleDownload(row)}
                                data-testid={`action-download-${row.code}`}
                              >
                                <Download className="w-4 h-4 mr-2" /> Download PNG
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => setDeleteTarget(row)}
                                data-testid={`action-delete-${row.code}`}
                                className="text-rose-600 focus:text-rose-600 focus:bg-rose-50 dark:focus:bg-rose-500/10"
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Hapus QR
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <CreateQRDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => {
          setCreateOpen(false);
          load();
        }}
      />
      <ScanQRDialog
        open={scanOpen}
        onOpenChange={setScanOpen}
        onResolved={(qr) => {
          setScanOpen(false);
          if (qr.locked) {
            toast.info(`QR ${qr.code} terkunci. Buka kunci untuk mengedit.`);
          }
          setEditTarget(qr);
        }}
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && !deleting && setDeleteTarget(null)}
      >
        <AlertDialogContent data-testid="delete-qr-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="jscan-brand text-2xl">
              Hapus QR{" "}
              <span className="jscan-mono text-rose-600 dark:text-rose-400 text-base">
                {deleteTarget?.code}
              </span>
              ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.status === "aktif" || deleteTarget?.status === "terkunci" ? (
                <>
                  <span className="text-rose-600 dark:text-rose-400 font-medium">
                    Peringatan:
                  </span>{" "}
                  QR ini sudah aktif dengan tujuan{" "}
                  <span className="jscan-mono text-xs">{deleteTarget?.destination_url}</span>
                  . Jika akrilik sudah dijual ke UMKM, scan akan menampilkan "QR tidak
                  ditemukan". Aksi ini tidak bisa dibatalkan.
                </>
              ) : (
                <>
                  QR kosong ini akan dihapus permanen. Kode tidak bisa dipulihkan setelah
                  dihapus.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button" disabled={deleting}>
              Batal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              data-testid="delete-confirm-button"
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
            >
              {deleting ? "Menghapus..." : "Hapus permanen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <EditQRDialog
        target={editTarget}
        onOpenChange={(o) => !o && setEditTarget(null)}
        onSaved={() => {
          setEditTarget(null);
          load();
        }}
      />
    </div>
  );
}
